/**
 * Load More Button Component for Shopify Dawn Theme
 *
 * Replaces traditional numbered pagination with infinite-scroll style
 * "Load More" button functionality using AJAX to append products.
 *
 * Features:
 * - AJAX loading without page refresh
 * - Loading state with spinner
 * - Accessibility announcements for screen readers
 * - Preserves lazy loading and animations
 * - Works with Dawn's filtering/sorting system
 * - Handles edge cases and errors gracefully
 */

class LoadMoreButton extends HTMLElement {
  constructor() {
    super();

    // Bind methods to preserve 'this' context
    this.handleClick = this.handleClick.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
  }

  connectedCallback() {
    // Cache DOM elements for performance
    this.button = this.querySelector('.load-more__button');
    this.buttonText = this.querySelector('.load-more__text');
    this.spinner = this.querySelector('.load-more__spinner');
    this.statusContainer = this.querySelector('.load-more__status');
    this.countDisplay = this.querySelector('.load-more__count');

    // Get configuration from data attributes
    this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
    this.sectionId = this.dataset.sectionId;
    this.collectionUrl = this.dataset.collectionUrl;

    // Track state
    this.isLoading = false;
    this.loadedProductsCount = this.getInitialProductCount();

    // Initialize event listeners
    this.initializeEventListeners();
  }

  disconnectedCallback() {
    // Clean up event listeners
    this.removeEventListeners();
  }

  /**
   * Initialize all event listeners
   */
  initializeEventListeners() {
    if (this.button) {
      this.button.addEventListener('click', this.handleClick);
    }

    // Listen for filter/sort changes to reset pagination state
    // Dawn uses history.pushState for filter updates
    window.addEventListener('popstate', this.handleFilterChange);
  }

  /**
   * Remove event listeners on disconnect
   */
  removeEventListeners() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleClick);
    }
    window.removeEventListener('popstate', this.handleFilterChange);
  }

  /**
   * Get the initial count of products displayed
   * @returns {number} Number of products currently in the grid
   */
  getInitialProductCount() {
    const productGrid = document.getElementById('product-grid');
    if (productGrid) {
      return productGrid.querySelectorAll('.grid__item').length;
    }
    return 0;
  }

  /**
   * Handle the Load More button click
   * @param {Event} event - Click event
   */
  async handleClick(event) {
    event.preventDefault();

    // Prevent multiple simultaneous requests
    if (this.isLoading) return;

    const nextUrl = this.button.dataset.nextUrl;
    if (!nextUrl) return;

    await this.loadMoreProducts(nextUrl);
  }

  /**
   * Handle filter/sort changes by resetting state
   */
  handleFilterChange() {
    // When filters change, the entire ProductGridContainer is replaced
    // by Dawn's facets.js, so we need to update our reference
    this.currentPage = 1;
    this.loadedProductsCount = this.getInitialProductCount();
  }

  /**
   * Load more products via AJAX
   * @param {string} url - URL to fetch next page of products
   */
  async loadMoreProducts(url) {
    this.setLoadingState(true);

    try {
      // Build the section URL for AJAX request
      // This uses Shopify's section rendering API
      const sectionUrl = this.buildSectionUrl(url);

      const response = await fetch(sectionUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Parse the response and append new products
      this.appendProducts(html);

      // Update the button state for next page
      this.updateButtonState(html);

      // Update product count display
      this.updateProductCount();

      // Announce to screen readers
      this.announceNewProducts();

      // Trigger scroll animations if enabled
      this.triggerAnimations();

    } catch (error) {
      console.error('Load More Error:', error);
      this.handleError();
    } finally {
      this.setLoadingState(false);
    }
  }

  /**
   * Build the section URL with current filters/sort
   * @param {string} baseUrl - Base pagination URL
   * @returns {string} Complete URL with section_id parameter
   */
  buildSectionUrl(baseUrl) {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  }

  /**
   * Parse HTML response and append new products to grid
   * @param {string} html - HTML response from server
   */
  appendProducts(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Get the product grid from the response
    const newProductGrid = doc.getElementById('product-grid');
    if (!newProductGrid) {
      console.error('Product grid not found in response');
      return;
    }

    // Get current product grid
    const currentProductGrid = document.getElementById('product-grid');
    if (!currentProductGrid) {
      console.error('Current product grid not found');
      return;
    }

    // Get all new product items
    const newProducts = newProductGrid.querySelectorAll('.grid__item');

    // Calculate animation order offset based on existing products
    const existingProductCount = currentProductGrid.querySelectorAll('.grid__item').length;

    // Append each new product to the grid
    newProducts.forEach((product, index) => {
      // Clone the product node
      const productClone = product.cloneNode(true);

      // Update animation order for scroll animations
      if (productClone.hasAttribute('data-cascade')) {
        productClone.style.setProperty('--animation-order', existingProductCount + index + 1);
      }

      // Ensure lazy loading is enabled for new products
      const images = productClone.querySelectorAll('img[loading="lazy"]');
      images.forEach(img => {
        // Reset src to trigger lazy loading
        if (img.dataset.src) {
          img.src = img.dataset.src;
        }
      });

      // Append to grid
      currentProductGrid.appendChild(productClone);
    });

    // Update loaded count
    this.loadedProductsCount += newProducts.length;
    this.currentPage++;
  }

  /**
   * Update button state based on response (hide if no more pages)
   * @param {string} html - HTML response from server
   */
  updateButtonState(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Check for load more button in response
    const newLoadMore = doc.querySelector('load-more-button');

    if (newLoadMore) {
      // Get the new button if it exists
      const newButton = newLoadMore.querySelector('.load-more__button');

      if (newButton && newButton.dataset.nextUrl) {
        // Update next URL for subsequent loads
        this.button.dataset.nextUrl = newButton.dataset.nextUrl;
      } else {
        // No more pages - hide the button
        this.hideButton();
      }
    } else {
      // No load more component in response - we've loaded all products
      this.hideButton();
    }
  }

  /**
   * Hide the Load More button when all products are loaded
   */
  hideButton() {
    if (this.button) {
      this.button.style.display = 'none';
      this.button.setAttribute('aria-hidden', 'true');
    }

    // Update status message
    if (this.statusContainer) {
      const allLoadedMessage = this.statusContainer.dataset.allLoadedMessage || 'All products loaded';
      this.countDisplay.textContent = allLoadedMessage;
    }
  }

  /**
   * Update the product count display
   */
  updateProductCount() {
    if (!this.countDisplay) return;

    // Get total product count from the page
    const totalProducts = this.getTotalProductCount();

    // Update the count display
    const countText = `Showing ${this.loadedProductsCount} of ${totalProducts} products`;
    this.countDisplay.textContent = countText;
  }

  /**
   * Get total product count from the page
   * @returns {number} Total number of products in collection
   */
  getTotalProductCount() {
    // Try to get from ProductCount element (used by Dawn)
    const productCountEl = document.getElementById('ProductCount');
    if (productCountEl) {
      const match = productCountEl.textContent.match(/(\d+)/);
      if (match) {
        return parseInt(match[0], 10);
      }
    }

    // Fallback: estimate from pages
    const productsPerPage = this.loadedProductsCount / this.currentPage;
    return Math.round(productsPerPage * this.totalPages);
  }

  /**
   * Set loading state on button
   * @param {boolean} loading - Whether loading is in progress
   */
  setLoadingState(loading) {
    this.isLoading = loading;

    if (!this.button) return;

    if (loading) {
      this.button.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      this.buttonText?.classList.add('hidden');
      this.spinner?.classList.remove('hidden');
    } else {
      this.button.setAttribute('aria-busy', 'false');
      this.button.disabled = false;
      this.buttonText?.classList.remove('hidden');
      this.spinner?.classList.add('hidden');
    }
  }

  /**
   * Announce new products to screen readers
   */
  announceNewProducts() {
    if (!this.statusContainer) return;

    // Create announcement for screen readers
    const announcement = document.createElement('span');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'visually-hidden';
    announcement.textContent = `More products loaded. Now showing ${this.loadedProductsCount} products.`;

    // Add to status container temporarily
    this.statusContainer.appendChild(announcement);

    // Remove after announcement is made
    setTimeout(() => {
      announcement.remove();
    }, 1000);
  }

  /**
   * Trigger scroll animations for newly added products
   */
  triggerAnimations() {
    // Check if scroll animations are enabled
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger();
    }

    // Also trigger any IntersectionObserver-based animations
    const productGrid = document.getElementById('product-grid');
    if (productGrid) {
      const newItems = productGrid.querySelectorAll('.scroll-trigger:not(.scroll-trigger--cancel)');
      newItems.forEach(item => {
        // Trigger the animation class
        if (item.classList.contains('scroll-trigger')) {
          // Small delay to allow DOM to settle
          setTimeout(() => {
            item.classList.add('scroll-trigger--cancel');
          }, 100);
        }
      });
    }
  }

  /**
   * Handle errors during product loading
   */
  handleError() {
    // Show error message to user
    if (this.buttonText) {
      const originalText = this.buttonText.textContent;
      this.buttonText.textContent = 'Error loading products. Try again.';

      // Reset after 3 seconds
      setTimeout(() => {
        this.buttonText.textContent = originalText;
      }, 3000);
    }

    // Announce error to screen readers
    if (this.statusContainer) {
      const errorAnnouncement = document.createElement('span');
      errorAnnouncement.setAttribute('role', 'alert');
      errorAnnouncement.className = 'visually-hidden';
      errorAnnouncement.textContent = 'Error loading more products. Please try again.';
      this.statusContainer.appendChild(errorAnnouncement);

      setTimeout(() => {
        errorAnnouncement.remove();
      }, 3000);
    }
  }
}

// Register the custom element
if (!customElements.get('load-more-button')) {
  customElements.define('load-more-button', LoadMoreButton);
}

/**
 * Integration with Dawn's FacetFiltersForm
 *
 * When filters are applied, Dawn replaces the entire ProductGridContainer.
 * We need to ensure the Load More button is properly re-initialized.
 */
(function() {
  // Store reference to original renderProductGridContainer if it exists
  if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.renderProductGridContainer) {
    const originalRenderProductGridContainer = FacetFiltersForm.renderProductGridContainer;

    FacetFiltersForm.renderProductGridContainer = function(html) {
      // Call original function
      originalRenderProductGridContainer.call(this, html);

      // The Load More component will be re-created automatically
      // since we're using a custom element that re-initializes on connectedCallback
    };
  }
})();
